import { pileDePolice } from '../../lib/polices';
import { adresseNette } from '../../lib/adresse';
import { texteDescription } from '../../lib/texte-enrichi';
import { QRCode } from '../../components/QRCode.jsx';
import { ScaleFit } from '../../components/ScaleFit.jsx';
import { SOC } from '../../constants/company';
import { PAYS_BY } from '../../constants/countries';
import { DEVIS_STYLES, HL_MAP } from '../../constants/documents';
import { DEVISE, fmt } from '../../lib/currency';
import { styleDuMessage } from '../../lib/styles-messages';
import { tr, trf } from '../../lib/i18n';
import { RESEAUX, reseauLabel, reseauURL } from '../../lib/payments';
import { printDoc } from '../../lib/print';
import { epcQR, swissQR } from '../../lib/swiss-qr';
import { qrNational, qrPlatba, qrZbp } from '../../lib/qr-nationaux';
import { SYSTEMES_QR, systemeQrUtilisable } from '../../constants/paiements-qr';
import { estBloc } from '../../lib/blocs-lignes';
import { docRef } from '../../lib/swiss-refs';
import { termesOffre } from '../../lib/terminologie';
import { TAUX_LEGAUX_ACTUELS } from '../../lib/tva';
import { acompteDocument, auCentime, pourcentLisible, totauxDocument } from '../../lib/vat';
import { acompteDemandeDe } from '../../lib/acompte-facture';
import { prixApplique } from '../../lib/paliers';
import { C, DA } from '../../theme/palette';

/**
 * L'identité à porter sur un document, selon son secteur.
 *
 * <p>En mode mixte, chaque secteur a ses coordonnées et son IBAN. Le document
 * recevait l'identité de l'univers ouvert à l'écran : une facture de magasin
 * consultée depuis le bâtiment affichait l'IBAN du bâtiment, alors que le PDF
 * et la QR-facture du serveur portaient le bon.
 *
 * @param {object} soc société, avec ses profils par secteur
 * @param {object} doc document affiché
 * @returns {object} la société aux coordonnées du secteur du document
 */
export function identitePourDocument(soc, doc) {
  const profils = soc?.profils;
  if (!profils || !doc?.sect) return soc;
  const profil = profils[doc.sect === 'magasin' ? 'magasin' : 'chantier'] || {};
  return {
    ...soc,
    ...Object.fromEntries(Object.entries(profil).filter(([, v]) => v !== undefined && v !== '')),
  };
}

/**
 * Le document A4 : devis, offre, bon de commande, facture ou rappel.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.doc document affiché
 * @param {object} props.soc société émettrice, avec ses profils par secteur
 * @param {object} props.client destinataire
 * @param {Function} props.onClose retour à l'écran précédent
 * @param {boolean} [props.preview] sans barre d'actions ni fond gris : la
 *   feuille seule, pour l'aperçu des réglages
 * @param {boolean} [props.statique] rendu pour le PDF officiel, hors de tout
 *   navigateur : la feuille seule, sans section de paiement — le serveur y
 *   joint la QR-facture suisse à ses dimensions légales, au bas de la dernière
 *   page ou sur une page de plus
 * @param {import('react').ReactNode} [props.annexe] annexe affichée sous la
 *   feuille, comme les photos jointes à l'offre ; ignorée en statique, où le
 *   service de rendu pose la sienne
 * @returns {JSX.Element} le document
 */
/**
 * IBAN par groupes de quatre caractères (« SA03 8000 0000 … »), la forme
 * imprimée des banques.
 *
 * @param {string} iban IBAN saisi, avec ou sans espaces
 * @returns {string} l'IBAN groupé, vide sans IBAN
 */
export function ibanLisible(iban) {
  const brut = String(iban || '')
    .replace(/\s+/g, '')
    .toUpperCase();
  // Un numéro de compte national (Inde, États-Unis…) n'est pas un IBAN : il
  // reste tel que saisi.
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{8,}$/.test(brut)) return String(iban || '').trim();
  return brut.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * Le pied légal du document : raison sociale · forme · RC · TVA · IBAN.
 *
 * <p>La forme juridique n'est pas répétée quand la raison sociale la porte
 * déjà : « Beka Rénovation Sàrl · Sàrl » se lisait sur chaque document d'une
 * société nommée avec sa forme, c'est-à-dire presque toutes.
 *
 * <p>Dernier bloc de la feuille, à l'écran comme dans le PDF
 * ({@code rendu/entree.jsx} la soude au bloc qui la précède).
 *
 * @param {object} soc société émettrice, telle que l'écran la lit
 * @returns {string} la ligne, ou une chaîne vide sans forme, RC ni TVA
 */
export function ligneLegale(soc) {
  // « N° TVA sur le document » décoché : le numéro sort aussi du pied.
  const tva = soc?.afficher?.socTva === false ? '' : soc?.tva;
  if (!(soc?.forme || soc?.rc || tva)) return '';
  const nom = soc?.nom || SOC.nom;
  const forme = soc?.forme;
  const formeUtile =
    forme &&
    forme !== 'Indépendant' &&
    forme !== 'Autre' &&
    !new RegExp(
      '(^|[\\s(,])' + forme.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '([\\s),.]|$)',
      'i',
    ).test(nom);
  return [nom, formeUtile ? forme : null, soc?.rc, tva, soc?.iban || SOC.iban]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Date d'échéance d'une facture : sa date plus le délai (« 30 jours »).
 *
 * @param {string} date date de la facture, JJ.MM.AAAA
 * @param {string} delai « 30 jours » ; tout autre délai (« À réception ») n'a pas de date
 * @returns {string} la date JJ.MM.AAAA, ou une chaîne vide
 */
export function dateDEcheance(date, delai) {
  const d = String(date || '').match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  const n = String(delai || '').match(/^(\d+) jours$/);
  if (!d || !n) return '';
  const e = new Date(+d[3], +d[2] - 1, +d[1] + +n[1]);
  return (
    String(e.getDate()).padStart(2, '0') +
    '.' +
    String(e.getMonth() + 1).padStart(2, '0') +
    '.' +
    e.getFullYear()
  );
}

/**
 * Une ligne dont la description passe une dizaine de lignes écrites : à
 * l'impression, elle peut se couper entre deux pages au lieu de partir entière
 * à la suivante en laissant un grand blanc (environ 60 signes par ligne dans
 * la colonne Désignation).
 *
 * @param {object} l ligne du document
 * @returns {boolean} vrai pour une longue description
 */
export function ligneLongue(l) {
  const lignes = String(l?.sd || '')
    .split('\n')
    .reduce((n, p) => n + Math.max(1, Math.ceil(p.length / 60)), 0);
  return lignes > 10;
}

/** Deux libellés qui disent la même chose, à la casse et aux espaces près. */
function memeTexte(a, b) {
  const net = (t) =>
    String(t || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  return net(a) !== '' && net(a) === net(b);
}
export function DocumentA4({
  doc,
  soc: socRecue,
  client: clientRecu,
  onClose,
  preview,
  statique,
  annexe,
}) {
  const soc = identitePourDocument(socRecue, doc);
  // L'adresse du client remise en ordre : une rue répétée devant le NPA ne
  // s'imprime pas en double (lib/adresse.js).
  const client = clientRecu && { ...clientRecu, ...adresseNette(clientRecu.adr, clientRecu.npa) };
  // Le document rendu par le serveur est la même feuille, mais la section de
  // paiement de l'écran — réduite pour tenir dans la page — y cède la place à
  // la QR-facture aux dimensions de la norme, que le serveur pose au bas de
  // la dernière page. Sans cela, le client recevrait deux bulletins.
  const bulletinDansLaFeuille = !statique;
  const accent = doc.accent || (DEVIS_STYLES.find((s) => s.id === doc.style) || {}).accent || DA;
  const estMag = doc.sect ? doc.sect === 'magasin' : !!doc.estMag;
  // Le nom du chantier lie, ou la description du devis (lib/projet.js) :
  // l'identifiant brut du chantier ne disait rien au client, et la facture
  // convertie d'un devis sortait avec « Projet — ».
  const projet = doc.projet || '—';
  // Le numéro de bon de commande du client, en vue sous le titre : la société
  // qui commande ne paie que la facture qui le porte.
  const commandeClient = String(doc.commandeClient ?? '').trim();
  // La garantie, telle que Réglages → Documents la règle (case « Garantie »,
  // durée en années) : elle n'était imprimée que sur deux modèles, en dur à
  // deux ans, et jamais sur le modèle suisse.
  const anneesGarantie = Math.max(0, Math.round(+(soc?.garantieAns ?? 2) || 0));
  const avecGarantie = soc?.afficher?.garantie !== false && anneesGarantie > 0;
  // Réglages → Documents → « Éléments affichés » : le n° TVA s'enlève du
  // document ; le téléphone et l'e-mail de la société s'y ajoutent, cochés.
  const afficheSoc = soc?.afficher || {};
  const tvaSoc = afficheSoc.socTva === false ? '' : soc?.tva || SOC.tva;
  const telSoc = afficheSoc.socTel === false ? '' : soc?.tel || SOC.tel;
  const emailSoc = afficheSoc.socEmail === false ? '' : soc?.email || SOC.email;
  /** Les valeurs non vides, séparées d'un point médian. */
  const enLigne = (...v) => v.filter(Boolean).join(' · ');
  const dureeGarantie = anneesGarantie === 1 ? tr('1 an') : trf('{0} ans', anneesGarantie);
  const articleGarantie = estMag ? tr('art. 210 CO') : tr('art. 371 CO');
  const blocCommande = commandeClient ? (
    <div
      data-bon-commande
      style={{
        display: 'inline-block',
        border: '1.5px solid ' + C.ink,
        borderRadius: 5,
        padding: '4px 10px',
        marginTop: 6,
        marginBottom: 2,
        fontSize: 10.5,
        color: C.ink,
      }}
    >
      {tr('Votre bon de commande :')} <b>{commandeClient}</b>
    </div>
  ) : null;
  const _h2r = (h) => {
    h = (h || '#000').replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16) || 0,
      parseInt(h.slice(2, 4), 16) || 0,
      parseInt(h.slice(4, 6), 16) || 0,
    ];
  };
  const _lum = (h) => {
    const [r, g, b] = _h2r(h);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  };
  const _dark = (h, f) => {
    const [r, g, b] = _h2r(h);
    const d = (x) => Math.round(x * (1 - f));
    return '#' + [d(r), d(g), d(b)].map((x) => x.toString(16).padStart(2, '0')).join('');
  };
  const accTxt = _lum(accent) > 0.6 ? '#2A2A2A' : '#fff'; // texte lisible sur fond accent
  const accInk = _lum(accent) > 0.55 ? _dark(accent, 0.5) : accent; // accent foncé pour titres/traits sur blanc
  const lay = (DEVIS_STYLES.find((s) => s.id === doc.style) || {}).lay || 'suisse';
  const swiss =
    lay === 'suisse' ||
    lay === 'filet' ||
    lay === 'edserif' ||
    lay === 'cadre' ||
    lay === 'filigrane' ||
    lay === 'titreDroite' ||
    lay === 'logoCentre' ||
    lay === 'bandeauFin' ||
    lay === 'encadreTitre' ||
    lay === 'titreGauche' ||
    lay === 'doubleFilet';
  const sheetFont =
    soc?.docFont && soc.docFont !== ''
      ? pileDePolice(soc.docFont)
      : lay === 'editorial' || lay === 'edserif'
        ? pileDePolice("Georgia,'Times New Roman',serif")
        : 'inherit';
  // styles de tableau différents selon le modèle
  const tHeadFill = lay === 'bande'; // entête de tableau en couleur
  const tZebra = false; // lignes alternées
  const tRowBorder = !(lay === 'minimal' || lay === 'editorial'); // bordures de lignes
  const tRowLeft = lay === 'barre'; // filet de couleur à gauche
  const thBg = tHeadFill ? accent : 'transparent';
  const thColor = tHeadFill ? accTxt : swiss ? C.ink : C.muted;
  const thBorder = tHeadFill ? 'none' : '2px solid ' + (lay === 'editorial' ? accInk : C.ink);
  /**
   * Net d'une ligne : quantité × prix unitaire, remise de ligne déduite. Le
   * prix est celui du palier atteint, comme le PDF du serveur l'imprime.
   */
  const lnet = (l) => (+l.q || 0) * prixApplique(l) * (1 - (+l.rem || 0) / 100);
  // Les memes totaux que les ecrans, et que le serveur : c'est ce document que
  // le client recoit, et le QR en porte le montant.
  const _tv = totauxDocument(doc.lignes, doc.tvaPct, doc.remisePct);
  const brut = _tv.htBrut;
  const rabaisLignes = _tv.rabaisLignes;
  const sousTotal = auCentime(brut - rabaisLignes);
  const remise = _tv.remise;
  const apresRemise = _tv.ht;
  const tvaDetail = _tv.detail,
    multiTva = _tv.multi,
    tvaBase = _tv.base,
    tva = _tv.tva;
  const ttc = _tv.ttc;
  // Comme le serveur, au centime : celui du QR et de la facture d'acompte.
  const acompte = acompteDocument(
    doc.lignes,
    doc.tvaPct,
    doc.remisePct,
    doc.acomptePct,
    doc.acompteMontant,
  );
  const recu = doc.recu || 0;
  const solde = ttc - recu;
  // L'acompte demandé sur la facture (30 % ou CHF 3'000.00) : tant qu'il
  // n'est pas payé, le QR le demande, et le solde suit à la fin des travaux.
  const acompteDemande = doc.kind === 'facture' && !doc.rappel ? acompteDemandeDe(doc, ttc) : 0;
  const resteAcompte = Math.max(0, auCentime(acompteDemande - recu));
  const fraisRap = (doc.rappel && +doc.rappel.frais) || 0;
  const isFac = doc.kind === 'facture';
  // Paiements reçus sur la facture, à leur date (le PDF du serveur les joint).
  const paiementsRecus = isFac && !doc.rappel && Array.isArray(doc.paiements) ? doc.paiements : [];
  // Versé avant la date de la facture : un acompte.
  const jourIso = (d) =>
    String(d || '')
      .split('.')
      .reverse()
      .join('-');
  const avantLaFacture = (d) => !!doc.date && jourIso(d) < jourIso(doc.date);
  // Le nom de la pièce suit Réglages → Documents (Offre / Devis / Auto) : la
  // même règle que l'écran des devis, le courriel et la page du client.
  const termes = termesOffre(soc, { estMag });
  // Le nom de la pièce dans la langue du document : « Offre » → « Offerte ».
  // L'avoir annule tout ou partie d'une facture : il se titre « Avoir », et
  // n'appelle aucun versement du client.
  const estAvoir = isFac && doc.type === 'avoir';
  const motDoc = doc.bonCommande
    ? tr('Bon de commande')
    : estAvoir
      ? tr('Avoir')
      : isFac
        ? tr('Facture')
        : tr(termes.Mot);
  const titre = doc.bonCommande
    ? tr('BON DE COMMANDE')
    : estAvoir
      ? tr('AVOIR')
      : isFac
        ? tr('FACTURE')
        : tr(termes.Mot).toUpperCase();
  const _imprimer = () => printDoc();
  const hasRab = rabaisLignes > 0;
  // Payée, la facture demande ce qu'elle demandait à l'envoi : le total,
  // moins les acomptes versés avant elle.
  const acomptesAvant = paiementsRecus
    .filter((p) => avantLaFacture(p.date))
    .reduce((s, p) => s + (+p.montant || 0), 0);
  const montantQR =
    (isFac && !doc.rappel && !!doc.paye
      ? Math.max(0, auCentime(ttc - acomptesAvant))
      : isFac
        ? resteAcompte > 0
          ? resteAcompte
          : recu > 0
            ? solde
            : ttc
        : doc.acomptePct > 0
          ? acompte
          : ttc) + fraisRap;
  const _pays = soc?.pays || 'CH';
  // Le réglage « Sur le document, afficher » décide, dans chaque pays :
  // QR-code, IBAN seul, ou les deux. Sans réglage, la Suisse et les pays de
  // l'euro portent leur QR-code. Le PDF du serveur suit le même réglage.
  // Le pays sous l'adresse : celui de l'entreprise, et celui du client quand
  // sa fiche le dit — une entreprise de Paris n'écrit pas « Suisse ».
  const paysSoc = tr((PAYS_BY[_pays] || {}).nom || 'Suisse');
  const paysClient = client?.pays ? tr((PAYS_BY[client.pays] || {}).nom || client.pays) : paysSoc;
  // Le délai arrive en français (« 30 jours », « À réception ») : il se lit
  // dans la langue du document.
  // Sur une facture, l'échéance est une date (« 14.11.2026 »), comme sur le
  // PDF du serveur : un délai seul obligeait le client à compter.
  const delai = doc.ech
    ? /^\d+ jours$/.test(doc.ech)
      ? trf('{0} jours', parseInt(doc.ech, 10))
      : tr(doc.ech)
    : tr('30 jours');
  // Un avoir ne se paie pas : pas d'échéance à lui donner.
  const echeance = estAvoir
    ? '—'
    : (isFac && dateDEcheance(doc.date, doc.ech || '30 jours')) || delai;
  // Sans IBAN, aucune section de paiement : un QR-code bâti sur un compte
  // manquant est illisible au guichet, et la facture paraîtrait payable alors
  // qu'elle ne l'est pas. Le serveur applique la même règle au PDF figé.
  const ibanSoc = (soc?.iban || SOC.iban || '').trim();
  // Une offre n'appelle un versement que si elle demande un acompte : sans
  // acompte, pas de section de paiement — un QR du total inviterait à payer
  // des travaux ni commandés ni faits. Le PDF du serveur suit la même règle.
  // Une facture payée porte « Payé » et « Net à payer 0.00 », et garde son
  // QR-facture tel qu'il est parti chez le client : « la facture a été
  // envoyée avec le QR code ». Il demande ce qui restait à l'envoi.
  const payee = isFac && !doc.rappel && !!doc.paye;
  const montantPaye = +doc.montantPaye > 0 ? +doc.montantPaye : ttc;
  const sansVersement = (!isFac && !(doc.acomptePct > 0)) || estAvoir;
  // Le QR code du pays (voir constants/paiements-qr) : QR-facture en Suisse,
  // QR-code européen, QR calculé depuis l'identifiant du pays (UPI, Pix…) ou
  // QR délivré par la banque. « IBAN uniquement », ou rien de ce qu'il faut
  // pour le calculer : la facture porte les coordonnées bancaires seules.
  const systemeQr =
    doc.noPay || doc.bonCommande || !ibanSoc || sansVersement
      ? null
      : systemeQrUtilisable({ ...soc, iban: ibanSoc, pays: _pays });
  const qrKind =
    systemeQr === 'SUISSE' ? 'ch' : systemeQr === 'EPC' ? 'epc' : systemeQr ? 'autre' : null;
  // Hors de Suisse, le QR code et l'IBAN vont ensemble, dans le même cadre (à
  // l'écran) ou dans la bande du bas (PDF, posée par le serveur).
  const avecQr = !!qrKind && qrKind !== 'ch';
  // Les coordonnées seules : sans QR code. Avec « QR code + IBAN », l'IBAN
  // figure dans le QR-facture suisse (« Compte / Payable à ») ou dans la bande
  // du QR code des autres pays : pas de ligne en plus, qui repoussait le
  // QR-facture sur une autre page.
  // « Bulletin de ma banque » que SwissTech ne peut pas refaire (QR code
  // illisible, ou pays sans QR code calculable) : le bulletin remis par la
  // banque, imprimé tel quel ; sans bulletin joint, l'IBAN seul. Lu, il fait le
  // QR code du pays ci-dessus, rempli pour chaque pièce.
  const bulletinBanque =
    !doc.noPay &&
    !doc.bonCommande &&
    !sansVersement &&
    !!ibanSoc &&
    soc?.paiement === 'bulletin' &&
    !systemeQr &&
    String(soc?.bulletinImage || '').startsWith('data:image/');
  const montreIban =
    !doc.noPay && !doc.bonCommande && !sansVersement && !!ibanSoc && !qrKind && !bulletinBanque;
  // Son QR code lu, le bulletin officiel reprend le titulaire tel que la
  // banque l'a écrit (« Compte / Payable à ») ; sinon, l'entreprise.
  const luBanque = soc?.paiement === 'bulletin' && soc?.bulletinLu ? soc.bulletinLu : null;
  const titulaire = {
    nom: luBanque?.nom || soc?.nom || SOC.nom,
    adr: luBanque?.adresse || soc?.adr || SOC.adr,
    npa: luBanque?.npa || soc?.npa || SOC.npa,
  };
  const contenuQr =
    systemeQr === 'EPC'
      ? epcQR({
          bic: soc?.bic,
          nom: soc?.nom || SOC.nom,
          iban: ibanSoc,
          amount: montantQR,
          ref: docRef(doc.refId || doc.num, ibanSoc).ref,
          msg: (isFac ? tr('Facture') : tr('Acompte offre n°')) + ' ' + (doc.num || ''),
        })
      : systemeQr === 'QR_PLATBA'
        ? qrPlatba({
            iban: ibanSoc,
            nom: soc?.nom || SOC.nom,
            montant: montantQR,
            devise: soc?.devise,
            reference: doc.num,
            message: (isFac ? tr('Facture') : tr('Acompte offre n°')) + ' ' + (doc.num || ''),
          })
        : systemeQr === 'ZBP'
          ? qrZbp({
              iban: ibanSoc,
              nom: soc?.nom || SOC.nom,
              montant: montantQR,
              titre: (isFac ? tr('Facture') : tr('Acompte offre n°')) + ' ' + (doc.num || ''),
            })
          : avecQr && systemeQr !== 'BANQUE'
            ? qrNational({
                systeme: systemeQr,
                identifiant: soc?.qrId,
                banque: soc?.qrBanque,
                nom: soc?.nom || SOC.nom,
                ville: String(soc?.npa || '').replace(/^\S+\s+/, ''),
                montant: montantQR,
                reference: docRef(doc.refId || doc.num, ibanSoc).ref,
              })
            : null;
  // La référence de paiement se dérive du numéro, sauf quand le serveur en
  // donne l'identifiant : l'acompte d'une offre et sa facture d'acompte
  // partagent une référence propre, distincte des numéros de facture.
  const dref0 = docRef(doc.refId || doc.num, ibanSoc);
  const dref =
    doc.refOv && String(doc.refOv).trim()
      ? { ...dref0, ref: String(doc.refOv).trim(), aff: String(doc.refOv).trim() }
      : dref0;
  const HEAD = {
    fontSize: 8,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: accInk,
    borderBottom: '1px solid ' + C.border,
    paddingBottom: 3,
    marginBottom: 5,
  };
  const LN = { fontSize: 8.5, color: C.grey, lineHeight: 1.5 };
  return (
    <div
      style={
        preview
          ? { background: '#fff' }
          : { height: '100%', display: 'flex', flexDirection: 'column', background: '#5b5f66' }
      }
    >
      <style>
        {
          '@media print{@page{size:A4;margin:14mm}body *{visibility:hidden}.doc-sheet,.doc-sheet *{visibility:visible}.doc-sheet{position:absolute;left:0;top:0;width:100%;box-shadow:none!important}.doc-noprint{display:none!important}tr{page-break-inside:avoid}tr.ligne-longue{page-break-inside:auto;break-inside:auto}.qr-scale-inner{transform:none!important;width:auto!important}.qr-scale-wrap{height:auto!important;overflow:visible!important}}'
        }
      </style>
      {!preview && (
        <div
          className="doc-noprint"
          style={{
            background: C.navy,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <button
            onClick={onClose}
            aria-label={tr('Retour')}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ←
          </button>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>
            {tr('Document A4 ·')} {doc.num}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={_imprimer}
              style={{
                background: 'rgba(255,255,255,.18)',
                border: 'none',
                color: '#fff',
                borderRadius: 8,
                padding: '7px 14px',
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {tr('🖨 Imprimer')}
            </button>
          </div>
        </div>
      )}
      {!preview && (
        <div
          className="doc-noprint"
          style={{
            background: C.navyXL,
            borderBottom: '1px solid ' + C.border,
            padding: '6px 14px',
            fontSize: 9,
            color: C.muted,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {tr(
            '🖨 Imprimer = envoie vers votre imprimante (ou « Enregistrer en PDF »). Pour envoyer au client : bouton Envoyer dans le détail.',
          )}
        </div>
      )}
      <div style={preview ? { padding: 0 } : { flex: 1, overflowY: 'auto', padding: '14px 10px' }}>
        <div
          className="doc-sheet"
          // La feuille suit la langue du document, pas la traduction de l'écran ;
          // sa mise en page (adresses, bulletin de versement) reste de gauche à droite.
          translate="no"
          dir="ltr"
          style={{
            position: 'relative',
            isolation: 'isolate',
            maxWidth: 680,
            margin: '0 auto',
            background: '#fff',
            borderRadius: 4,
            padding: '15px 18px',
            // Écriture normale : le nom des articles, les titres et les totaux
            // portent leur propre gras. Avec 600 partout, le PDF (police sans
            // demi-gras) écrivait toute la feuille en gras, quel que soit le modèle.
            fontWeight: 400,
            fontFamily: sheetFont,
            // Pas de glyphes de remplacement (« 4×40 », tirets et parenthèses
            // « de capitales ») : le PDF les rendait en caractères privés, et
            // le texte copié ou lu du PDF — n° de bon de commande, articles
            // repris — n'était plus celui écrit.
            fontVariantLigatures: 'none',
            fontFeatureSettings: '"calt" 0, "case" 0, "liga" 0',
            zoom: soc?.docSize || 1,
            borderLeft: lay === 'filet' ? '7px solid ' + accent : undefined,
            border: lay === 'cadre' ? '1px solid ' + accInk : undefined,
          }}
        >
          {soc?.filigrane?.actif !== false && soc?.logo && (
            <img
              src={soc.logo}
              className="doc-filigrane"
              aria-hidden="true"
              alt=""
              style={{
                position: 'absolute',
                top: (soc?.filigrane?.y ?? 44) + '%',
                left: (soc?.filigrane?.x ?? 50) + '%',
                transform:
                  'translate(-50%,-50%) rotate(' + (soc?.filigrane?.rotation || 0) + 'deg)',
                width: (soc?.filigrane?.taille ?? 62) + '%',
                maxWidth: 560,
                maxHeight: '78%',
                objectFit: 'contain',
                opacity: (soc?.filigrane?.opacite ?? 12) / 100,
                pointerEvents: 'none',
                // Au-dessus du contenu, en « produit » : posé dessous, le bloc
                // plein du Total TTC le recouvrait et le logo paraissait coupé ;
                // en multiplication, il reste entier sans voiler le texte.
                zIndex: 1,
                mixBlendMode: 'multiply',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
              }}
            />
          )}
          {lay === 'filigrane' && !soc?.logo && (soc?.nom || SOC.nom) && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: (soc?.filigrane?.y ?? 46) + '%',
                left: (soc?.filigrane?.x ?? 50) + '%',
                transform:
                  'translate(-50%,-50%) rotate(' + (soc?.filigrane?.rotation ?? -20) + 'deg)',
                fontSize: 46,
                fontWeight: 900,
                color: accInk,
                opacity: (soc?.filigrane?.opacite ?? 6) / 100,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 1,
                mixBlendMode: 'multiply',
              }}
            >
              {soc?.nom || SOC.nom}
            </div>
          )}
          {doc.rappel && (
            <div
              className="qr-keep"
              style={{
                background: C.redL,
                border: '1px solid ' + C.red,
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 900, color: C.red, letterSpacing: 0.5 }}>
                {doc.rappel.niveau >= 3
                  ? tr('DERNIER RAPPEL')
                  : doc.rappel.niveau === 2
                    ? tr('2e RAPPEL')
                    : tr('RAPPEL')}
              </div>
              <div style={{ fontSize: 9, color: C.ink, marginTop: 4, lineHeight: 1.5 }}>
                {/* Une seule phrase à traduire : coupée en deux, l'allemand
                    disait « ist die Rechnung F-1 ist bis heute unbezahlt ». */}
                {trf(
                  'Sauf erreur de notre part, la facture {0} demeure impayée à ce jour. Nous vous prions de bien vouloir procéder au règlement dans les meilleurs délais.',
                  doc.num,
                )}
                {fraisRap > 0
                  ? ' ' +
                    trf(
                      'Des frais de rappel de {0} {1} ont été ajoutés au montant dû.',
                      DEVISE,
                      fmt(fraisRap),
                    )
                  : ''}
              </div>
              <div style={{ fontSize: 9.5, fontWeight: 800, color: C.ink, marginTop: 7 }}>
                {tr('Montant total à régler :')}{' '}
                <span style={{ color: C.red, fontWeight: 900 }}>
                  {DEVISE} {fmt(montantQR)}
                </span>
              </div>
            </div>
          )}
          {/* en-tête — varie selon le style choisi */}
          {lay === 'bande' ? (
            <div
              style={{
                margin: '-22px -20px 14px',
                background: accent,
                padding: '20px',
                color: accTxt,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                {soc?.logo ? (
                  <img
                    src={soc.logo}
                    style={{
                      width: 44,
                      height: 44,
                      objectFit: 'contain',
                      background: '#fff',
                      borderRadius: 8,
                      padding: 3,
                    }}
                    alt=""
                  />
                ) : (
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: 'rgba(0,0,0,.10)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: accTxt,
                      fontWeight: 900,
                      fontSize: 20,
                    }}
                  >
                    {(soc?.nom || SOC.nom).charAt(0)}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                  <div style={{ fontSize: 8, opacity: 0.85 }}>
                    {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                  </div>
                  <div style={{ fontSize: 8, opacity: 0.85 }}>{enLigne(telSoc, tvaSoc)}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -1, lineHeight: 1 }}>
                  {titre}
                </div>
                <div style={{ fontSize: 8, marginTop: 6 }}>
                  {[
                    ['N°', doc.num],
                    [tr('Date'), doc.date],
                    [isFac ? tr('Échéance') : tr('Validité'), echeance],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <span style={{ opacity: 0.8 }}>{k}</span>
                      <b>{v}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : lay === 'centre' ? (
            <div
              style={{
                textAlign: 'center',
                paddingBottom: 14,
                borderBottom: '1px solid ' + C.border,
                marginBottom: 2,
              }}
            >
              {soc?.logo ? (
                <img
                  src={soc.logo}
                  style={{
                    width: 54,
                    height: 54,
                    objectFit: 'contain',
                    margin: '0 auto 6px',
                    display: 'block',
                  }}
                  alt=""
                />
              ) : (
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 12,
                    background: accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: accTxt,
                    fontWeight: 900,
                    fontSize: 24,
                    margin: '0 auto 6px',
                  }}
                >
                  {(soc?.nom || SOC.nom).charAt(0)}
                </div>
              )}
              <div style={{ fontSize: 17, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
              <div style={{ ...LN }}>
                {enLigne(soc?.adr || SOC.adr, soc?.npa || SOC.npa, telSoc)}
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: accInk,
                  marginTop: 10,
                  letterSpacing: 1,
                }}
              >
                {titre}
              </div>
              <div style={{ width: 50, height: 2.5, background: accInk, margin: '5px auto 0' }} />
              <div style={{ fontSize: 8, marginTop: 6, color: C.muted }}>
                N° {doc.num} · {doc.date} ·{' '}
                {(isFac ? tr('Échéance') : tr('Validité')) + ' ' + echeance}
              </div>
            </div>
          ) : lay === 'minimal' ? (
            <div>
              {soc?.logo && (
                <img
                  src={soc.logo}
                  style={{ height: 30, objectFit: 'contain', marginBottom: 6, display: 'block' }}
                  alt=""
                />
              )}
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <div style={{ fontSize: 13, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                <div style={{ fontSize: 8, color: C.muted }}>{tvaSoc}</div>
              </div>
              <div style={{ ...LN }}>
                {enLigne(soc?.adr || SOC.adr, soc?.npa || SOC.npa, telSoc)}
              </div>
              <div style={{ height: 2, background: accInk, margin: '10px 0' }} />
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 300,
                    letterSpacing: 3,
                    color: accInk,
                    textTransform: 'uppercase',
                  }}
                >
                  {titre}
                </div>
                <div style={{ fontSize: 8, textAlign: 'right' }}>
                  {[
                    ['N°', doc.num],
                    [tr('Date'), doc.date],
                    [isFac ? tr('Échéance') : tr('Validité'), echeance],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <span style={{ color: C.muted }}>{k} </span>
                      <b>{v}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : lay === 'editorial' ? (
            <div
              style={{
                textAlign: 'center',
                paddingBottom: 14,
                marginBottom: 4,
                borderBottom: '3px double ' + accInk,
              }}
            >
              {soc?.logo && (
                <img
                  src={soc.logo}
                  style={{
                    height: 34,
                    objectFit: 'contain',
                    margin: '0 auto 6px',
                    display: 'block',
                  }}
                  alt=""
                />
              )}
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  color: C.muted,
                }}
              >
                {soc?.nom || SOC.nom}
              </div>
              <div
                style={{
                  fontFamily: "Georgia,'Times New Roman',serif",
                  fontSize: 30,
                  fontWeight: 700,
                  color: accInk,
                  margin: '6px 0 4px',
                }}
              >
                {titre}
              </div>
              <div style={{ fontSize: 8, color: C.muted }}>
                N° {doc.num} · {doc.date} ·{' '}
                {(isFac ? tr('Échéance') : tr('Validité')) + ' ' + echeance}
              </div>
              <div style={{ ...LN, marginTop: 4 }}>
                {enLigne(soc?.adr || SOC.adr, soc?.npa || SOC.npa, telSoc)}
              </div>
            </div>
          ) : lay === 'epure' ? (
            <div style={{ marginBottom: 8 }}>
              {soc?.logo && (
                <img
                  src={soc.logo}
                  style={{ height: 30, objectFit: 'contain', marginBottom: 8, display: 'block' }}
                  alt=""
                />
              )}
              <div
                style={{
                  fontSize: 27,
                  fontWeight: 800,
                  color: accInk,
                  letterSpacing: -1,
                  lineHeight: 1,
                }}
              >
                {titre}
              </div>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 3 }}>
                N° {doc.num} · {doc.date} ·{' '}
                {(isFac ? tr('Échéance') : tr('Validité')) + ' ' + echeance}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginTop: 11,
                  paddingTop: 9,
                  borderTop: '1px solid ' + C.border,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                  <div style={{ ...LN }}>
                    {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                  </div>
                  <div style={{ ...LN }}>{enLigne(telSoc, emailSoc)}</div>
                </div>
                <div style={{ fontSize: 8, color: C.muted, textAlign: 'right' }}>{tvaSoc}</div>
              </div>
            </div>
          ) : lay === 'simple' ? (
            <div style={{ marginBottom: 8 }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {soc?.logo ? (
                    <img
                      src={soc.logo}
                      style={{ width: 40, height: 40, objectFit: 'contain' }}
                      alt=""
                    />
                  ) : (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 9,
                        background: accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accTxt,
                        fontWeight: 900,
                        fontSize: 18,
                      }}
                    >
                      {(soc?.nom || SOC.nom).charAt(0)}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                    <div style={{ ...LN }}>
                      {enLigne(soc?.adr || SOC.adr, soc?.npa || SOC.npa, telSoc)}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 23, fontWeight: 800, color: accInk, letterSpacing: -0.5 }}>
                  {titre}
                </div>
              </div>
              <div style={{ height: 2, background: accent, borderRadius: 2, marginTop: 9 }} />
              <div style={{ display: 'flex', gap: 16, fontSize: 8, color: C.muted, marginTop: 5 }}>
                <span>
                  N° <b style={{ color: C.ink }}>{doc.num}</b>
                </span>
                <span>
                  {tr('Date')} <b style={{ color: C.ink }}>{doc.date}</b>
                </span>
                <span>
                  {isFac ? tr('Échéance') : tr('Validité')}{' '}
                  <b style={{ color: C.ink }}>{echeance}</b>
                </span>
              </div>
            </div>
          ) : lay === 'moderne' ? (
            <div style={{ display: 'flex', gap: 14, alignItems: 'stretch', marginBottom: 8 }}>
              <div
                style={{
                  background: accent,
                  color: accTxt,
                  borderRadius: 12,
                  padding: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minWidth: 124,
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    opacity: 0.85,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  {titre}
                </div>
                <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1, marginTop: 4 }}>
                  {soc?.nom || SOC.nom}
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <div style={{ ...LN }}>
                  {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                </div>
                <div style={{ ...LN }}>{enLigne(telSoc, emailSoc)}</div>
                <div style={{ ...LN }}>{tvaSoc}</div>
                <div style={{ fontSize: 8, color: C.muted, marginTop: 5 }}>
                  N° {doc.num} · {doc.date} ·{' '}
                  {(isFac ? tr('Échéance') : tr('Validité')) + ' ' + echeance}
                </div>
              </div>
            </div>
          ) : lay === 'barre' ? (
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: 8,
                paddingBottom: 12,
                borderBottom: '1px solid ' + C.border,
              }}
            >
              <div style={{ width: 6, borderRadius: 6, background: accent, flexShrink: 0 }} />
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  {soc?.logo && (
                    <img
                      src={soc.logo}
                      style={{
                        height: 34,
                        objectFit: 'contain',
                        marginBottom: 5,
                        display: 'block',
                      }}
                      alt=""
                    />
                  )}
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                  <div style={{ ...LN }}>
                    {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                  </div>
                  <div style={{ ...LN }}>{enLigne(telSoc, tvaSoc)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: accInk, letterSpacing: -1 }}>
                    {titre}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted, marginTop: 4 }}>
                    {[
                      ['N°', doc.num],
                      [tr('Date'), doc.date],
                      [isFac ? tr('Échéance') : tr('Validité'), echeance],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        <span>{k}</span>
                        <b>{v}</b>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : swiss ? (
            <div style={{ marginBottom: 8 }}>
              {(() => {
                const big = lay === 'filet' || lay === 'filigrane';
                const centered = lay === 'edserif' || lay === 'cadre';
                const T = motDoc;
                const socB = (
                  <div style={{ flex: '0 0 52%', fontSize: 9, lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 800 }}>
                      {(() => {
                        const nm = (soc?.nom || SOC.nom || '').trim();
                        const fm =
                          soc?.forme && soc.forme !== 'Indépendant' && soc.forme !== 'Autre'
                            ? soc.forme
                            : '';
                        return fm && !nm.toLowerCase().includes(fm.toLowerCase())
                          ? nm + ' ' + fm
                          : nm;
                      })()}
                    </div>
                    <div>{soc?.adr || SOC.adr}</div>
                    <div>{soc?.npa || SOC.npa}</div>
                    <div>{paysSoc}</div>
                    {/* Ce modèle ne les portait pas : ils s'y ajoutent quand
                        le patron les coche dans Réglages → Documents. */}
                    {afficheSoc.socTel === true && telSoc && <div>{telSoc}</div>}
                    {afficheSoc.socEmail === true && emailSoc && <div>{emailSoc}</div>}
                  </div>
                );
                const cliB = (
                  <div style={{ flex: 1, fontSize: 9.5, lineHeight: 1.55, marginTop: 26 }}>
                    <div>{client?.nom || '—'}</div>
                    {client?.adr && <div>{client.adr}</div>}
                    {client?.npa && <div>{client.npa}</div>}
                    <div>{paysClient}</div>
                  </div>
                );
                const parties = (
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10 }}>
                    {socB}
                    {cliB}
                  </div>
                );
                if (centered)
                  return (
                    <>
                      {soc?.logo && (
                        <img
                          src={soc.logo}
                          style={{
                            height: 54,
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto 12px',
                          }}
                          alt=""
                        />
                      )}
                      <div
                        style={{
                          textAlign: 'center',
                          fontSize: 24,
                          fontWeight: 800,
                          letterSpacing: 6,
                          color: accInk,
                        }}
                      >
                        {T.toUpperCase()}
                      </div>
                      <div
                        style={{ textAlign: 'center', fontSize: 8.5, color: C.grey, marginTop: 3 }}
                      >
                        N° {doc.num} · {doc.date} {isFac ? tr('· Échéance') : tr('· Validité')}{' '}
                        {echeance}
                      </div>
                      <div style={{ borderTop: '1px solid ' + C.border, margin: '16px 0' }} />
                      {parties}
                    </>
                  );
                if (big)
                  return (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          borderBottom: '2px solid ' + accInk,
                          paddingBottom: 10,
                          marginBottom: 18,
                        }}
                      >
                        <div>
                          {soc?.logo && (
                            <img
                              src={soc.logo}
                              style={{ height: 46, objectFit: 'contain', display: 'block' }}
                              alt=""
                            />
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{ fontSize: 30, fontWeight: 800, color: accInk, lineHeight: 1 }}
                          >
                            {T}
                          </div>
                          <div style={{ fontSize: 9, color: C.grey, marginTop: 2 }}>
                            N° {doc.num} · {doc.date}
                          </div>
                        </div>
                      </div>
                      {parties}
                    </>
                  );
                if (lay === 'titreDroite')
                  return (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          marginBottom: 16,
                        }}
                      >
                        {soc?.logo && (
                          <img
                            src={soc.logo}
                            style={{ height: 48, objectFit: 'contain', display: 'block' }}
                            alt=""
                          />
                        )}
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{ fontSize: 34, fontWeight: 800, color: accInk, lineHeight: 1 }}
                          >
                            {T}
                          </div>
                          <div style={{ fontSize: 9, color: C.grey, marginTop: 4 }}>
                            N° {doc.num} · {doc.date} {isFac ? tr('· Échéance') : tr('· Validité')}{' '}
                            {echeance}
                          </div>
                        </div>
                      </div>
                      <div style={{ borderTop: '1px solid ' + C.border, marginBottom: 14 }} />
                      {parties}
                    </>
                  );
                if (lay === 'logoCentre')
                  return (
                    <>
                      {soc?.logo && (
                        <img
                          src={soc.logo}
                          style={{
                            height: 54,
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto 10px',
                          }}
                          alt=""
                        />
                      )}
                      <div
                        style={{
                          borderTop: '2px solid ' + accInk,
                          borderBottom: '2px solid ' + accInk,
                          padding: '8px 0',
                          textAlign: 'center',
                          fontSize: 16,
                          fontWeight: 800,
                          letterSpacing: 4,
                          color: accInk,
                          marginBottom: 16,
                        }}
                      >
                        {T.toUpperCase()} · N° {doc.num}
                      </div>
                      {parties}
                    </>
                  );
                if (lay === 'bandeauFin')
                  return (
                    <>
                      <div
                        style={{
                          background: accInk,
                          color: accTxt,
                          padding: '10px 14px',
                          borderRadius: 4,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          marginBottom: 15,
                        }}
                      >
                        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: 1.5 }}>
                          {T.toUpperCase()}
                        </span>
                        <span style={{ fontSize: 9 }}>
                          N° {doc.num} · {doc.date}
                        </span>
                      </div>
                      {soc?.logo && (
                        <img
                          src={soc.logo}
                          style={{
                            height: 44,
                            objectFit: 'contain',
                            display: 'block',
                            marginBottom: 12,
                          }}
                          alt=""
                        />
                      )}
                      {parties}
                    </>
                  );
                if (lay === 'encadreTitre')
                  return (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 16,
                        }}
                      >
                        {soc?.logo && (
                          <img
                            src={soc.logo}
                            style={{ height: 48, objectFit: 'contain', display: 'block' }}
                            alt=""
                          />
                        )}
                        <div
                          style={{
                            border: '2px solid ' + accInk,
                            borderRadius: 10,
                            padding: '8px 18px',
                            textAlign: 'center',
                          }}
                        >
                          <div
                            style={{
                              fontSize: 20,
                              fontWeight: 800,
                              color: accInk,
                              letterSpacing: 1,
                            }}
                          >
                            {T}
                          </div>
                          <div style={{ fontSize: 8, color: C.grey, marginTop: 1 }}>
                            N° {doc.num} · {doc.date}
                          </div>
                        </div>
                      </div>
                      {parties}
                    </>
                  );
                if (lay === 'titreGauche')
                  return (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          marginBottom: 16,
                        }}
                      >
                        <div>
                          <div
                            style={{ fontSize: 34, fontWeight: 800, color: accInk, lineHeight: 1 }}
                          >
                            {T}
                          </div>
                          <div style={{ fontSize: 9, color: C.grey, marginTop: 4 }}>
                            N° {doc.num} · {doc.date} {isFac ? tr('· Échéance') : tr('· Validité')}{' '}
                            {echeance}
                          </div>
                        </div>
                        {soc?.logo && (
                          <img
                            src={soc.logo}
                            style={{ height: 48, objectFit: 'contain', display: 'block' }}
                            alt=""
                          />
                        )}
                      </div>
                      <div style={{ borderTop: '1px solid ' + C.border, marginBottom: 14 }} />
                      {parties}
                    </>
                  );
                if (lay === 'doubleFilet')
                  return (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          marginBottom: 9,
                        }}
                      >
                        {soc?.logo && (
                          <img
                            src={soc.logo}
                            style={{ height: 46, objectFit: 'contain', display: 'block' }}
                            alt=""
                          />
                        )}
                        <div style={{ textAlign: 'right' }}>
                          <div
                            style={{
                              fontSize: 29,
                              fontWeight: 800,
                              color: accInk,
                              letterSpacing: 1,
                            }}
                          >
                            {T.toUpperCase()}
                          </div>
                          <div style={{ fontSize: 9, color: C.grey }}>
                            N° {doc.num} · {doc.date}
                          </div>
                        </div>
                      </div>
                      <div style={{ borderTop: '3px solid ' + accInk }} />
                      <div
                        style={{ borderTop: '1px solid ' + accInk, marginTop: 2, marginBottom: 14 }}
                      />
                      {parties}
                    </>
                  );
                return (
                  <>
                    {soc?.logo && (
                      <img
                        src={soc.logo}
                        style={{
                          height: 60,
                          objectFit: 'contain',
                          display: 'block',
                          marginBottom: 16,
                        }}
                        alt=""
                      />
                    )}
                    {parties}
                    <div
                      style={{
                        borderTop: '1px solid ' + C.ink,
                        paddingTop: 7,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 800 }}>
                        {motDoc} {doc.num}
                      </div>
                      <div style={{ fontSize: 9, color: C.grey }}>{tr('Page 1')}</div>
                    </div>
                  </>
                );
              })()}
              {/* L'objet, s'il y en a un : sans objet, le cadre du titre dit
                  déjà « Facture » ou « Offre », et la place libérée laisse la
                  QR-facture tenir au bas de la page. */}
              {doc.objet && (
                <div
                  style={{
                    borderTop: '1px solid ' + C.border,
                    borderBottom: '1px solid ' + C.border,
                    padding: '4px 0',
                    marginTop: 5,
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  {doc.objet}
                </div>
              )}
              {doc.plaque || doc.vehicule ? (
                <div
                  style={{
                    background: C.teal + '12',
                    border: '1px solid ' + C.teal,
                    borderRadius: 6,
                    padding: '5px 9px',
                    marginTop: 5,
                    fontSize: 9,
                  }}
                >
                  🚗{' '}
                  {doc.vehicule ? (
                    <>
                      <b style={{ color: C.teal }}>{tr('Véhicule :')}</b> {doc.vehicule}
                    </>
                  ) : null}
                  {doc.vehicule && doc.plaque ? '  ·  ' : ''}
                  {doc.plaque ? (
                    <>
                      <b style={{ color: C.teal }}>{tr('Plaque :')}</b> {doc.plaque}
                    </>
                  ) : null}
                </div>
              ) : null}
              {blocCommande}
              <div
                style={{
                  display: 'flex',
                  gap: 24,
                  padding: '6px 0 7px',
                  borderTop: doc.objet ? 'none' : '1px solid ' + C.border,
                  marginTop: doc.objet ? 0 : 5,
                  borderBottom: '1px solid ' + C.border,
                  fontSize: 8.5,
                  marginBottom: 9,
                }}
              >
                <div style={{ flex: 1 }}>
                  {[
                    [tr('Date'), doc.date],
                    [isFac ? tr('Échéance') : tr("Valable jusqu'au"), echeance],
                    // Le projet qui répète l'objet écrit juste au-dessus ne se
                    // redit pas : le patron le voyait deux fois de suite.
                    doc.projet && !memeTexte(doc.projet, doc.objet) ? [tr('Projet'), projet] : null,
                  ]
                    .filter(Boolean)
                    .map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
                        <span style={{ color: C.grey, minWidth: 74 }}>{k}</span>
                        <b>{v}</b>
                      </div>
                    ))}
                </div>
                <div style={{ flex: 1 }}>
                  {[
                    [tr('Établi par'), soc?.nom || SOC.nom],
                    ...(afficheSoc.socTva === false
                      ? []
                      : [[tr('N° TVA'), soc?.tva || SOC.tva || '—']]),
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
                      <span style={{ color: C.grey, minWidth: 78 }}>{k}</span>
                      <b>{v}</b>
                    </div>
                  ))}
                </div>
              </div>
              {doc.intro ? (
                <div
                  style={{
                    fontSize: 9.5,
                    whiteSpace: 'pre-line',
                    ...styleDuMessage(doc.stylesMessages, 'haut'),
                  }}
                >
                  {doc.intro}
                </div>
              ) : null}
              {doc.regie && doc.chLieu && (
                <div style={{ fontSize: 9.5, marginTop: 7 }}>
                  {tr('Travaux réalisés à :')} <b>{doc.chLieu}</b>
                </div>
              )}
              {doc.delai && (
                <div style={{ fontSize: 9.5, marginTop: 7 }}>
                  {estMag ? tr('Délai de livraison') : tr("Délai d'exécution")} : <b>{doc.delai}</b>
                </div>
              )}
            </div>
          ) : lay === 'vague' ? (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 8,
                  marginBottom: 10,
                  background: accent + '14',
                }}
              >
                <svg
                  viewBox="0 0 600 86"
                  preserveAspectRatio="none"
                  style={{ display: 'block', width: '100%', height: 58 }}
                >
                  <path d="M0,66 Q150,18 300,52 T600,38 L600,86 L0,86 Z" fill={accent + '66'} />
                  <path d="M0,76 Q180,40 360,62 T600,52 L600,86 L0,86 Z" fill={accent + '33'} />
                </svg>
                <div
                  style={{
                    position: 'absolute',
                    top: 7,
                    right: 16,
                    fontSize: 36,
                    fontWeight: 900,
                    letterSpacing: 5,
                    color: accInk,
                    opacity: 0.2,
                  }}
                >
                  {titre}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {soc?.logo ? (
                    <img
                      src={soc.logo}
                      style={{ width: 42, height: 42, objectFit: 'contain' }}
                      alt=""
                    />
                  ) : (
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 9,
                        background: accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accTxt,
                        fontWeight: 900,
                        fontSize: 19,
                      }}
                    >
                      {(soc?.nom || SOC.nom).charAt(0)}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                    <div style={{ ...LN }}>
                      {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                    </div>
                    <div style={{ ...LN }}>{enLigne(telSoc, tvaSoc)}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: accInk }}>{titre}</div>
                  <div style={{ fontSize: 8, color: C.muted, marginTop: 3 }}>
                    N° {doc.num} · {doc.date}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>
                    {(isFac ? tr('Échéance') : tr('Validité')) + ' ' + echeance}
                  </div>
                </div>
              </div>
            </div>
          ) : lay === 'structure' ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                paddingBottom: 11,
                borderBottom: '3px solid ' + accInk,
                marginBottom: 4,
              }}
            >
              <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                {soc?.logo ? (
                  <img
                    src={soc.logo}
                    style={{ width: 46, height: 46, objectFit: 'contain' }}
                    alt=""
                  />
                ) : (
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 10,
                      background: accent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: accTxt,
                      fontWeight: 900,
                      fontSize: 21,
                    }}
                  >
                    {(soc?.nom || SOC.nom).charAt(0)}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                  <div style={{ ...LN }}>
                    {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                  </div>
                  <div style={{ ...LN }}>{enLigne(telSoc, emailSoc)}</div>
                  <div style={{ ...LN }}>{tvaSoc}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right', minWidth: 130 }}>
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    color: accInk,
                    letterSpacing: -1,
                    lineHeight: 1,
                  }}
                >
                  {titre}
                </div>
                <div style={{ marginTop: 7 }}>
                  {[
                    [tr('NUMÉRO'), doc.num],
                    ['CHANTIER', projet],
                    ['DATE', doc.date],
                    [isFac ? tr('ÉCHÉANCE') : tr('VALABLE'), echeance],
                  ].map(([k, v]) => (
                    <div key={k} style={{ marginBottom: 3 }}>
                      <div
                        style={{
                          fontSize: 6.5,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                          color: C.muted,
                        }}
                      >
                        {k}
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 800, color: C.ink }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : lay === 'pro' ? (
            <div style={{ marginBottom: 8 }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                  {soc?.logo ? (
                    <img
                      src={soc.logo}
                      style={{ width: 42, height: 42, objectFit: 'contain' }}
                      alt=""
                    />
                  ) : (
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 9,
                        background: accInk,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: 19,
                      }}
                    >
                      {(soc?.nom || SOC.nom).charAt(0)}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                    <div style={{ fontSize: 7.5, color: C.muted }}>
                      {enLigne(soc?.adr || SOC.adr, soc?.npa || SOC.npa, telSoc)}
                    </div>
                    <div style={{ fontSize: 7.5, color: C.muted }}>{enLigne(emailSoc, tvaSoc)}</div>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 34,
                    fontWeight: 900,
                    color: accInk,
                    letterSpacing: -1.5,
                    lineHeight: 0.9,
                  }}
                >
                  {titre}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 14,
                  fontSize: 8,
                  color: C.muted,
                  marginTop: 6,
                }}
              >
                <span>
                  N° <b style={{ color: C.ink }}>{doc.num}</b>
                </span>
                <span>{doc.date}</span>
                <span>
                  {isFac ? tr('Échéance') : tr('Validité')}{' '}
                  <b style={{ color: C.ink }}>{echeance}</b>
                </span>
              </div>
            </div>
          ) : lay === 'encadre' ? (
            <div
              style={{
                border: '2px solid ' + accInk,
                borderRadius: 12,
                padding: '13px 15px',
                marginBottom: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                {soc?.logo ? (
                  <img
                    src={soc.logo}
                    style={{ width: 44, height: 44, objectFit: 'contain' }}
                    alt=""
                  />
                ) : (
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: accent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: accTxt,
                      fontWeight: 900,
                      fontSize: 20,
                    }}
                  >
                    {(soc?.nom || SOC.nom).charAt(0)}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                  <div style={{ ...LN }}>
                    {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                  </div>
                  <div style={{ ...LN }}>{enLigne(telSoc, emailSoc)}</div>
                  <div style={{ ...LN }}>{tvaSoc}</div>
                </div>
              </div>
              <div
                style={{
                  textAlign: 'right',
                  background: accent,
                  color: accTxt,
                  borderRadius: 9,
                  padding: '9px 13px',
                  minWidth: 118,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1 }}>
                  {titre}
                </div>
                <div style={{ fontSize: 8, marginTop: 6 }}>
                  {[
                    ['N°', doc.num],
                    [tr('Date'), doc.date],
                    [isFac ? tr('Échéance') : tr('Validité'), echeance],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
                    >
                      <span style={{ opacity: 0.85 }}>{k}</span>
                      <b>{v}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                paddingBottom: 12,
                borderBottom: '2px solid ' + C.ink,
              }}
            >
              <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                {soc?.logo ? (
                  <img
                    src={soc.logo}
                    style={{ width: 46, height: 46, objectFit: 'contain' }}
                    alt=""
                  />
                ) : (
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 10,
                      background: C.red,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: 22,
                    }}
                  >
                    S
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.4 }}>
                    {soc?.nom || SOC.nom}
                  </div>
                  <div style={{ ...LN }}>
                    {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                  </div>
                  <div style={{ ...LN }}>{enLigne(telSoc, emailSoc)}</div>
                  <div style={{ ...LN }}>{tvaSoc}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    letterSpacing: -1,
                    lineHeight: 1,
                    color: accInk,
                  }}
                >
                  {titre}
                </div>
                <div
                  style={{ width: 40, height: 2.5, background: accInk, margin: '6px 0 6px auto' }}
                />
                <div style={{ fontSize: 8 }}>
                  {[
                    ['N°', doc.num],
                    [tr('Chantier'), projet],
                    [tr('Date'), doc.date],
                    [isFac ? tr('Échéance') : tr('Validité'), echeance],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8,
                        padding: '1px 0',
                      }}
                    >
                      <span style={{ color: C.muted }}>{k}</span>
                      <b>{v}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* parties */}
          {!swiss && (
            <div style={{ margin: '14px 0' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 16,
                  alignItems: 'start',
                }}
              >
                <div>
                  <div style={HEAD}>{isFac ? tr('Facture de') : tr('Offre de')}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 2 }}>
                    {soc?.nom || SOC.nom}
                  </div>
                  <div style={LN}>{soc?.adr || SOC.adr}</div>
                  <div style={LN}>{soc?.npa || SOC.npa}</div>
                  <div style={LN}>{paysSoc}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={HEAD}>
                    {isFac
                      ? tr('Facturé à')
                      : termes.accord(
                          trf('{0} établi pour', tr(termes.Mot)),
                          trf('{0} établie pour', tr(termes.Mot)),
                        )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 2 }}>
                    {client?.nom || '—'}
                  </div>
                  <div style={LN}>{client?.adr}</div>
                  <div style={LN}>{client?.npa}</div>
                  {client?.tel && <div style={LN}>{client.tel}</div>}
                  {client?.email && (
                    <div style={{ ...LN, color: accInk, fontWeight: 600 }}>{client.email}</div>
                  )}
                </div>
              </div>
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 9,
                  borderTop: '1px solid ' + C.border,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 22px',
                  fontSize: 8.5,
                }}
              >
                <span>
                  <span style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>
                    {tr('Chantier')}{' '}
                  </span>
                  {projet}
                </span>
                {doc.chLieu && (
                  <span>
                    <span style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>
                      {tr('Lieu des travaux')}{' '}
                    </span>
                    {doc.chLieu}
                  </span>
                )}
                {doc.ref && (
                  <span>
                    <span style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>
                      {tr('Réf.')}{' '}
                    </span>
                    {doc.ref}
                  </span>
                )}
                {avecGarantie && (
                  <span>
                    <span style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>
                      {tr('Garantie')}{' '}
                    </span>
                    {dureeGarantie} — {articleGarantie}
                  </span>
                )}
                {
                  <span>
                    <span style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>
                      {tr('Normes')}{' '}
                    </span>
                    {tr('SIA 118')}
                  </span>
                }
              </div>
            </div>
          )}
          {!swiss && blocCommande && <div style={{ marginBottom: 10 }}>{blocCommande}</div>}
          {/* objet */}
          {doc.objet && !swiss && (
            <div
              style={{
                display: 'flex',
                borderRadius: 6,
                overflow: 'hidden',
                border: '1px solid ' + C.border,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  background: C.ink,
                  color: '#fff',
                  fontSize: 7.5,
                  fontWeight: 800,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  padding: '10px 11px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {tr('Objet')}
              </div>
              <div style={{ padding: '10px 12px', fontSize: 10, fontWeight: 600 }}>{doc.objet}</div>
            </div>
          )}
          {/* tableau — varie selon le modèle */}
          {(() => {
            const showTva = lay === 'vague' || lay === 'structure' || lay === 'pro';
            const showPos = swiss;
            const showRef = false;
            const cols = (showPos ? [tr('Pos.')] : [])
              .concat(showRef ? [tr('Réf.')] : [])
              .concat([tr('Désignation'), tr('Qté'), tr('P.U.')])
              .concat(showTva ? [tr('TVA')] : [])
              .concat(hasRab ? [tr('Rabais')] : [])
              .concat([tr('Total')]);
            const rb = 'none';
            return (
              <table
                className="doc-lignes"
                style={{
                  width: '100%',
                  borderCollapse: tHeadFill ? 'separate' : 'collapse',
                  borderSpacing: 0,
                  marginTop: 2,
                }}
              >
                <thead>
                  <tr>
                    {cols.map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign:
                            i < (showPos ? 1 : 0) + (showRef ? 1 : 0) + 1 ? 'left' : 'right',
                          fontSize: 9,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          color: thColor,
                          background: thBg,
                          borderBottom: thBorder,
                          padding: tHeadFill ? '5px 6px' : '3px 6px',
                          borderTopLeftRadius: tHeadFill && i === 0 ? 5 : 0,
                          borderBottomLeftRadius: tHeadFill && i === 0 ? 5 : 0,
                          borderTopRightRadius: tHeadFill && i === cols.length - 1 ? 5 : 0,
                          borderBottomRightRadius: tHeadFill && i === cols.length - 1 ? 5 : 0,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(doc.lignes || []).map((l, i) => {
                    const net = lnet(l);
                    const bg = tZebra && i % 2 === 1 ? accent + '18' : 'transparent';
                    return (
                      <tr
                        key={i}
                        className={ligneLongue(l) ? 'ligne-longue' : undefined}
                        style={{ background: l.hl && HL_MAP[l.hl] ? HL_MAP[l.hl].bg : bg }}
                      >
                        {showPos && (
                          <td
                            className="doc-pos"
                            style={{
                              padding: '5px 6px',
                              borderBottom: rb,
                              fontSize: 12,
                              color: C.muted,
                              verticalAlign: 'top',
                            }}
                          >
                            {i + 1}
                          </td>
                        )}
                        {showRef && (
                          <td
                            className="doc-ref"
                            style={{
                              padding: '5px 6px',
                              borderBottom: rb,
                              fontSize: 8,
                              color: C.muted,
                              verticalAlign: 'top',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {l.ref || '—'}
                          </td>
                        )}
                        <td
                          className="doc-desig"
                          style={{
                            padding: '5px 6px',
                            borderBottom: rb,
                            verticalAlign: 'top',
                            borderLeft:
                              l.hl && HL_MAP[l.hl]
                                ? '3px solid ' + HL_MAP[l.hl].c
                                : tRowLeft
                                  ? '3px solid ' + accent
                                  : 'none',
                          }}
                        >
                          {/* La photo de l'article à la hauteur
                              de l'article, et non en annexe en fin de document. */}
                          {l.photo &&
                            (() => {
                              const image = (
                                <img
                                  className="doc-photo-ligne"
                                  src={l.photo}
                                  alt=""
                                  style={{
                                    float: 'left',
                                    width: 46,
                                    height: 46,
                                    objectFit: 'contain',
                                    borderRadius: 5,
                                    border: '1px solid ' + C.border,
                                    background: '#fff',
                                    marginRight: 8,
                                  }}
                                />
                              );
                              // Dans le PDF du client, la photo s'ouvre en grand.
                              return l.photoLien ? <a href={l.photoLien}>{image}</a> : image;
                            })()}
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>
                            {l.hl && HL_MAP[l.hl] && (
                              <span
                                style={{
                                  fontSize: 7,
                                  fontWeight: 800,
                                  color: '#fff',
                                  background: HL_MAP[l.hl].c,
                                  borderRadius: 3,
                                  padding: '1px 5px',
                                  marginRight: 5,
                                  verticalAlign: 'middle',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {tr(HL_MAP[l.hl].lab)}
                              </span>
                            )}
                            {l.d || '—'}
                          </div>
                          {l.sd && (
                            <div
                              style={{
                                fontSize: 11.5,
                                color: C.ink,
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.35,
                                marginTop: 1,
                              }}
                            >
                              {texteDescription(l.sd)}
                            </div>
                          )}
                        </td>
                        <td
                          className={'doc-num' + (estBloc(l) ? ' doc-vide' : '')}
                          data-label={tr('Qté')}
                          style={{
                            padding: '5px 6px',
                            borderBottom: rb,
                            fontSize: 12,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {/* Un bloc se vend au prix global : ni « 1 bloc » ni
                              prix unitaire, seulement son total. */}
                          {estBloc(l) ? '' : l.q + ' ' + tr(l.u)}
                        </td>
                        <td
                          className={'doc-num' + (estBloc(l) ? ' doc-vide' : '')}
                          data-label={tr('P.U.')}
                          style={{
                            padding: '5px 6px',
                            borderBottom: rb,
                            fontSize: 12,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {estBloc(l) ? '' : fmt(prixApplique(l))}
                        </td>
                        {showTva && (
                          <td
                            className="doc-num"
                            data-label={tr('TVA')}
                            style={{
                              padding: '5px 6px',
                              borderBottom: rb,
                              fontSize: 11,
                              textAlign: 'right',
                              color: C.muted,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {l.taxe === false
                              ? '—'
                              : l.tva != null
                                ? +l.tva
                                : (doc.tvaPct ?? TAUX_LEGAUX_ACTUELS.normal)}
                            %
                          </td>
                        )}
                        {hasRab && (
                          <td
                            className="doc-num"
                            data-label={tr('Rabais')}
                            style={{
                              padding: '5px 6px',
                              borderBottom: rb,
                              fontSize: 12,
                              textAlign: 'right',
                              color: l.rem > 0 ? C.green : C.muted,
                            }}
                          >
                            {l.rem > 0 ? l.rem + '%' : '—'}
                          </td>
                        )}
                        <td
                          className="doc-num doc-total"
                          data-label={tr('Total')}
                          style={{
                            padding: '5px 6px',
                            borderBottom: rb,
                            fontSize: 12.5,
                            textAlign: 'right',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {fmt(net)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
          {/* totaux */}
          <div
            className="doc-bloc"
            style={{
              display: 'flex',
              justifyContent: doc.lienEnLigne?.url ? 'space-between' : 'flex-end',
              alignItems: doc.lienEnLigne?.url ? 'flex-end' : undefined,
              gap: doc.lienEnLigne?.url ? 12 : undefined,
              marginTop: 14,
            }}
          >
            {/* Le PDF envoyé tel quel ne dit pas quand il est ouvert : le bouton
                mène à la page en ligne de la pièce, dont chaque ouverture est
                notée chez l'entreprise (« ouvert depuis le PDF »). Il prend la
                place vide à gauche des totaux : la feuille ne grandit pas, et
                la QR-facture reste sur la même page. Le serveur ne le pose que
                sur le PDF, jamais sur un avoir. */}
            {doc.lienEnLigne?.url && (
              <div data-lien-en-ligne style={{ flex: '1 1 auto', minWidth: 0, paddingBottom: 4 }}>
                <a
                  href={doc.lienEnLigne.url}
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    // Les couleurs du « Total TTC » : lisible quelle que soit
                    // la couleur du document.
                    background: lay === 'pro' ? '#16241d' : accent,
                    color: lay === 'pro' ? '#fff' : accTxt,
                    borderRadius: 6,
                    fontWeight: 800,
                    fontSize: 9.5,
                    textDecoration: 'none',
                  }}
                >
                  {doc.lienEnLigne.libelle === 'payer'
                    ? tr('👁 Voir et payer en ligne')
                    : doc.lienEnLigne.libelle === 'accepter'
                      ? tr('👁 Voir et accepter en ligne')
                      : tr('👁 Voir en ligne')}
                </a>
                <div style={{ marginTop: 3, fontSize: 7, color: C.muted, wordBreak: 'break-all' }}>
                  {String(doc.lienEnLigne.url)
                    .replace(/^https?:\/\//, '')
                    .replace(/\?via=pdf$/, '')}
                </div>
              </div>
            )}
            <div style={{ flex: '0 0 250px' }}>
              {hasRab && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '3px 0',
                    fontSize: 9,
                  }}
                >
                  <span style={{ color: swiss ? C.grey : C.muted }}>{tr('Sous-total brut')}</span>
                  <span>{fmt(brut)}</span>
                </div>
              )}
              {hasRab && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '3px 0',
                    fontSize: 9,
                    color: C.green,
                  }}
                >
                  <span>{tr('Rabais lignes')}</span>
                  <span>− {fmt(rabaisLignes)}</span>
                </div>
              )}
              {/* Le total hors TVA avant la remise, puis la remise, puis le
                  total hors TVA remisé : le client voit ce qu'on lui offre. */}
              {doc.remisePct > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '3px 0',
                    fontSize: 9,
                  }}
                >
                  <span style={{ color: swiss ? C.grey : C.muted }}>
                    {hasRab ? tr('Sous-total HT') : tr('Total HT')}
                  </span>
                  <span>{fmt(sousTotal)}</span>
                </div>
              )}
              {doc.remisePct > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '3px 0',
                    fontSize: 9,
                    color: C.green,
                  }}
                >
                  <span>
                    {tr('Remise')} {pourcentLisible(doc.remisePct)}%
                  </span>
                  <span>− {fmt(remise)}</span>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '3px 0',
                  fontSize: 9,
                }}
              >
                <span style={{ color: swiss ? C.grey : C.muted }}>
                  {doc.remisePct > 0
                    ? tr('Total HT après remise')
                    : hasRab
                      ? tr('Total HT net')
                      : tr('Sous-total HT')}
                </span>
                <span>{fmt(apresRemise)}</span>
              </div>
              {multiTva ? (
                tvaDetail.map((x) => (
                  <div
                    key={x.taux}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '3px 0',
                      fontSize: 9,
                    }}
                  >
                    <span style={{ color: swiss ? C.grey : C.muted }}>
                      {trf('TVA {0}% (sur {1})', x.taux, fmt(x.ht))}
                    </span>
                    <span>{fmt(x.tva)}</span>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '3px 0',
                    fontSize: 9,
                  }}
                >
                  <span style={{ color: swiss ? C.grey : C.muted }}>
                    {tr('TVA')}{' '}
                    {(tvaDetail[0] || { taux: doc.tvaPct ?? TAUX_LEGAUX_ACTUELS.normal }).taux}%
                  </span>
                  <span>{fmt(tva)}</span>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 10px',
                  marginTop: 5,
                  background: lay === 'pro' ? '#16241d' : accent,
                  borderRadius: 6,
                  color: lay === 'pro' ? '#fff' : accTxt,
                }}
              >
                <span style={{ fontSize: 9, fontWeight: 700 }}>
                  {isFac ? tr('Total TTC') : tr('Total TTC')}
                </span>
                {/* Le montant ne se coupe jamais, même sous un libellé long. */}
                <span
                  style={{ fontSize: 13, fontWeight: 900, whiteSpace: 'nowrap', marginLeft: 8 }}
                >
                  {DEVISE} {fmt(ttc)}
                </span>
              </div>
              {/* Les paiements déjà reçus, chacun à sa date : l'acompte versé
                  en décembre pour des travaux facturés en février. */}
              {paiementsRecus.length > 0 && (
                <div data-testid="paiements-recus">
                  {paiementsRecus.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 0 0',
                        fontSize: 9,
                      }}
                    >
                      <span style={{ color: swiss ? C.grey : C.muted }}>
                        {trf(
                          avantLaFacture(p.date) ? 'Acompte versé le {0}' : 'Paiement reçu le {0}',
                          p.date,
                        )}
                      </span>
                      <span>− {fmt(p.montant)}</span>
                    </div>
                  ))}
                  {!payee && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 0 0',
                        fontSize: 10,
                        fontWeight: 800,
                      }}
                    >
                      <span>{tr('Reste à payer')}</span>
                      <span>
                        {DEVISE} {fmt(Math.max(0, solde))}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {resteAcompte > 0 && !payee && (
                <div data-testid="acompte-demande">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 0 0',
                      fontSize: 9,
                      color: C.amber,
                      fontWeight: 700,
                    }}
                  >
                    <span>
                      {tr('Acompte à payer maintenant')}
                      {+doc.acompteDemandeMontant > 0 ? '' : ' (' + doc.acompteDemande + ' %)'}
                    </span>
                    <span>
                      {DEVISE} {fmt(resteAcompte)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '3px 0 0',
                      fontSize: 9,
                      fontWeight: 700,
                    }}
                  >
                    <span>{tr('Solde à la fin des travaux')}</span>
                    <span>
                      {DEVISE} {fmt(Math.max(0, solde - resteAcompte))}
                    </span>
                  </div>
                </div>
              )}
              {payee && (
                <div className="doc-paye">
                  {paiementsRecus.length === 0 && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 0 0',
                        fontSize: 9,
                      }}
                    >
                      <span style={{ color: swiss ? C.grey : C.muted }}>{tr('Payé')}</span>
                      <span>{fmt(montantPaye)}</span>
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                      marginTop: 8,
                    }}
                  >
                    <span
                      data-testid="tampon-paye"
                      style={{
                        border: '2px solid ' + C.red,
                        color: C.red,
                        borderRadius: 7,
                        padding: '2px 12px',
                        fontSize: 15,
                        fontWeight: 900,
                        transform: 'rotate(-4deg)',
                      }}
                    >
                      {tr('Payé')}
                    </span>
                    <span style={{ fontSize: 12, color: C.red, whiteSpace: 'nowrap' }}>
                      {tr('Net à payer')}{' '}
                      <b>
                        {DEVISE} {fmt(Math.max(0, ttc - montantPaye))}
                      </b>
                    </span>
                  </div>
                </div>
              )}
              {doc.acomptePct > 0 && !isFac && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0 0',
                    fontSize: 9,
                    color: C.amber,
                    fontWeight: 700,
                  }}
                >
                  <span>
                    {tr('Acompte')}
                    {/* En francs, le montant suffit : « 30.54% » n'apprendrait rien au client. */}
                    {+doc.acompteMontant > 0 ? '' : ' ' + doc.acomptePct + '%'}{' '}
                    {doc.acompteDelai
                      ? trf('— payable sous {0} jours', doc.acompteDelai)
                      : tr('à la commande')}
                  </span>
                  <span>
                    {DEVISE} {fmt(acompte)}
                  </span>
                </div>
              )}
              {doc.acomptePct > 0 && !isFac && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '3px 0 0',
                    fontSize: 9,
                    color: C.ink,
                    fontWeight: 700,
                  }}
                >
                  <span>
                    {+doc.acompteMontant > 0
                      ? tr('Solde à la fin')
                      : trf('Solde ({0}%) à la fin', 100 - (doc.acomptePct || 0))}
                  </span>
                  <span>
                    {DEVISE} {fmt(ttc - acompte)}
                  </span>
                </div>
              )}
            </div>
          </div>
          {avecGarantie && (
            <div
              data-garantie
              style={{
                marginTop: 12,
                fontSize: 9.5,
                color: C.ink,
                lineHeight: 1.5,
              }}
            >
              <b>{tr('Garantie')} :</b> {dureeGarantie}{' '}
              {estMag ? tr('dès la livraison') : tr('dès la réception des travaux')} (
              {articleGarantie})
            </div>
          )}
          {/* mentions */}
          {doc.mentions && (
            <div
              style={{
                marginTop: 14,
                fontSize: 7,
                color: swiss ? C.grey : C.muted,
                lineHeight: 1.7,
                borderTop: '1px solid ' + C.border,
                paddingTop: 8,
                whiteSpace: 'pre-line',
              }}
            >
              {doc.mentions}
            </div>
          )}
          {/* L'offre signée par le client, sur son lien ou la tablette : sa
              signature, son nom, le jour et l'heure, sous « Bon pour accord ». */}
          {!isFac && doc.signatureClient && (
            <div
              className="doc-bloc"
              data-signature-client
              style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-start' }}
            >
              <div style={{ minWidth: 170, maxWidth: 240 }}>
                <div style={{ fontSize: 7.5, color: C.muted, fontWeight: 700 }}>
                  {tr('Le client — bon pour accord')}
                </div>
                {doc.signatureClient.trace ? (
                  <svg
                    viewBox="0 0 320 130"
                    role="img"
                    aria-label={tr('Signature du client')}
                    style={{ width: 170, height: 69, display: 'block' }}
                  >
                    <path
                      d={doc.signatureClient.trace}
                      fill="none"
                      stroke={C.navy}
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <div style={{ height: 42 }} />
                )}
                <div
                  style={{
                    fontSize: 8,
                    color: C.ink,
                    borderTop: '1px solid ' + C.border,
                    paddingTop: 3,
                    fontWeight: 700,
                  }}
                >
                  {doc.signatureClient.nom}
                </div>
                {doc.signatureClient.date && (
                  <div style={{ fontSize: 7, color: C.muted, marginTop: 1 }}>
                    {doc.signatureClient.heure
                      ? trf(
                          'Signé le {0} à {1}',
                          doc.signatureClient.date,
                          doc.signatureClient.heure,
                        )
                      : trf('Signé le {0}', doc.signatureClient.date)}
                  </div>
                )}
              </div>
            </div>
          )}
          {doc.tampon && (soc?.tampon || soc?.sigPatron) && (
            <div
              className="doc-bloc"
              style={{
                marginTop: 16,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 22,
                alignItems: 'flex-end',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                {soc?.sigPatron ? (
                  <img
                    src={soc.sigPatron}
                    alt="signature"
                    style={{ height: 42, objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ height: 42 }} />
                )}
                <div
                  style={{
                    fontSize: 7,
                    color: C.muted,
                    borderTop: '1px solid ' + C.border,
                    paddingTop: 3,
                    marginTop: 2,
                    minWidth: 130,
                  }}
                >
                  {soc?.nom || ''}
                </div>
              </div>
              {soc?.tampon && (
                <img
                  src={soc.tampon}
                  alt="tampon"
                  style={{ width: 72, height: 72, objectFit: 'contain' }}
                />
              )}
            </div>
          )}
          {(() => {
            const R = soc?.reseaux || {};
            const S = soc?.reseauxShow || {};
            const items = RESEAUX.filter((r) => R[r.key] && S[r.key] !== false);
            if (!items.length) return null;
            return (
              <div
                className="doc-bloc"
                style={{
                  marginTop: 10,
                  paddingTop: 7,
                  borderTop: '1px solid ' + C.border,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {items.map((r) => (
                  <a
                    key={r.key}
                    href={reseauURL(r.key, R[r.key])}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      textDecoration: 'none',
                      color: accInk,
                      fontSize: 8,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 11 }}>{r.icon}</span>
                    {reseauLabel(r.key, R[r.key])}
                  </a>
                ))}
              </div>
            );
          })()}
          {/* Le paiement vient après le document et ses liens, avant les
              photos : coordonnées du virement quand le réglage les demande,
              et, hors de Suisse, le QR-code européen dans le même cadre. Le
              bulletin suisse, lui, suit juste après (ou en bas du PDF). */}
          {/* Le cadre complet : sans QR-code dans le pays ou avec « IBAN
              seul » ; et, à l'écran, le QR-code européen, que le serveur pose
              en bande au bas du PDF. */}
          {((montreIban && !qrKind) || (avecQr && !statique)) && (
            <div
              className="qr-keep"
              style={{
                border: '1px solid ' + accInk,
                borderRadius: 6,
                marginTop: 10,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  background: accInk,
                  color: '#fff',
                  padding: '4px 12px',
                  fontWeight: 800,
                  fontSize: 8.5,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {!avecQr
                  ? tr('Paiement par virement bancaire')
                  : systemeQr === 'EPC'
                    ? tr('Paiement par virement — scannez le QR code')
                    : systemeQr === 'BANQUE'
                      ? tr('Paiement par QR code — scannez-le')
                      : trf('Paiement {0} — scannez le QR code', SYSTEMES_QR[systemeQr].nom)}
              </div>
              <div
                className={avecQr ? 'virement-grille-qr' : undefined}
                style={{
                  display: 'grid',
                  gridTemplateColumns: avecQr ? 'auto minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto',
                }}
              >
                {avecQr && (
                  <div
                    style={{
                      padding: '6px 0 6px 10px',
                      textAlign: 'center',
                    }}
                  >
                    {systemeQr === 'BANQUE' ? (
                      <img
                        src={soc.qrImage}
                        alt="QR"
                        style={{ width: 62, height: 62, objectFit: 'contain', display: 'block' }}
                      />
                    ) : (
                      <QRCode size={62} color={C.ink} value={contenuQr} />
                    )}
                  </div>
                )}
                <div style={{ padding: '6px 12px 4px', fontSize: 8.5 }}>
                  {[
                    [
                      tr('Bénéficiaire'),
                      <span key="beneficiaire">
                        <b>{soc?.nom || SOC.nom}</b>
                        {/* À côté du QR-code, le nom suffit : le cadre tient
                            alors sur la page de la facture. */}
                        {!avecQr && (
                          <>
                            <br />
                            {[soc?.adr || SOC.adr, soc?.npa || SOC.npa].filter(Boolean).join(', ')}
                          </>
                        )}
                      </span>,
                    ],
                    avecQr &&
                      SYSTEMES_QR[systemeQr].depuis === 'identifiant' && [
                        SYSTEMES_QR[systemeQr].nom,
                        <span key="identifiant" style={{ fontWeight: 800 }}>
                          {systemeQr === 'VIETQR' ? soc.qrBanque + ' · ' + soc.qrId : soc.qrId}
                        </span>,
                      ],
                    [
                      'IBAN',
                      <span
                        key="iban"
                        style={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          letterSpacing: 0.15,
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {/* Par groupes de quatre, comme sur un relevé : un IBAN
                            saisi d'un bloc débordait sur la référence. */}
                        {ibanLisible(soc?.iban) || '—'}
                      </span>,
                    ],

                    soc?.bic || soc?.banque
                      ? [
                          soc?.bic ? tr('BIC / Banque') : tr('Banque'),
                          <span key="banque">
                            {soc?.bic && <b>{soc.bic}</b>}
                            {soc?.bic && soc?.banque ? ' · ' : ''}
                            {soc?.banque || ''}
                          </span>,
                        ]
                      : null,
                  ]
                    .filter(Boolean)
                    .map(([lb, val]) => (
                      <div
                        key={lb}
                        style={{
                          display: 'flex',
                          gap: 8,
                          padding: '2px 0',
                          borderBottom: '1px solid ' + C.border,
                        }}
                      >
                        <div
                          style={{
                            width: 62,
                            flexShrink: 0,
                            fontSize: 6.5,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: C.muted,
                            paddingTop: 2,
                          }}
                        >
                          {lb}
                        </div>
                        <div style={{ minWidth: 0 }}>{val}</div>
                      </div>
                    ))}
                </div>
                <div
                  className="virement-montant"
                  style={{
                    width: avecQr ? 128 : 140,
                    padding: '6px 12px',
                    background: accInk + '10',
                    borderLeft: '1px solid ' + C.border,
                    fontSize: 8.5,
                  }}
                >
                  <div
                    style={{
                      fontSize: 6.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: C.muted,
                    }}
                  >
                    {tr('Montant à payer')}
                  </div>
                  <div
                    style={{ fontSize: 14, fontWeight: 900, color: accInk, margin: '2px 0 8px' }}
                  >
                    {DEVISE} {fmt(montantQR)}
                  </div>
                  <div
                    style={{
                      fontSize: 6.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: C.muted,
                    }}
                  >
                    {tr('Référence à indiquer')}
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 9.5, margin: '2px 0 8px' }}>
                    {dref.aff}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* QR-Bill (Suisse) ou coordonnées bancaires (autres pays) */}
          {/*
            Le bulletin suisse a des dimensions legales : il est dessine a sa
            largeur reelle, puis l'ensemble est reduit pour tenir dans
            l'apercu. Le laisser se reagencer sur un telephone donnait un
            bulletin dont le lecteur de la banque ne retrouve pas les reperes.
          */}
          {bulletinDansLaFeuille && bulletinBanque && (
            <div className="qr-keep" style={{ marginTop: 16 }}>
              <div
                style={{
                  borderTop: '1px dashed ' + C.ink,
                  fontSize: 6,
                  color: C.muted,
                  textAlign: 'center',
                  padding: '3px 0 6px',
                  letterSpacing: 1.2,
                }}
              >
                {tr('✂ — — — — — — — — — à détacher avant paiement — — — — — — — — —')}
              </div>
              <img
                src={soc.bulletinImage}
                alt={tr('Bulletin de versement')}
                style={{ width: '100%', display: 'block' }}
              />
            </div>
          )}
          {bulletinDansLaFeuille && qrKind === 'ch' && (
            <ScaleFit baseWidth={640}>
              <div className="qr-keep" style={{ marginTop: 16, zoom: 1 / (soc?.docSize || 1) }}>
                <div
                  style={{
                    borderTop: '1px dashed ' + C.ink,
                    fontSize: 6,
                    color: C.muted,
                    textAlign: 'center',
                    padding: '3px 0 6px',
                    letterSpacing: 1.2,
                  }}
                >
                  {tr('✂ — — — — — — — — — à détacher avant paiement — — — — — — — — —')}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '29.5% 70.5%',
                    border: '1px solid ' + C.ink,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      borderRight: '1px dashed ' + C.ink,
                      padding: '12px 12px',
                      fontSize: 7.5,
                      lineHeight: 1.35,
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 9, marginBottom: 8 }}>
                      {tr('Récépissé')}
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: 6,
                        marginBottom: 1,
                      }}
                    >
                      {tr('Compte / payable à')}
                    </div>
                    <div style={{ fontSize: 7, lineHeight: 1.3, marginBottom: 6 }}>
                      {soc?.iban || SOC.iban}
                      <br />
                      {titulaire.nom}
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: 6,
                        marginBottom: 1,
                      }}
                    >
                      {tr('Payable par')}
                    </div>
                    <div style={{ fontSize: 7, lineHeight: 1.3, marginBottom: 6 }}>
                      {client?.nom}
                      <br />
                      {client?.npa}
                    </div>
                    <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 6 }}>{tr('Monnaie')}</div>
                        {DEVISE}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 6 }}>{tr('Montant')}</div>
                        {fmt(montantQR)}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: '10px',
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 9, marginBottom: 8 }}>
                        {tr('Section paiement')}
                      </div>
                      <QRCode
                        size={118}
                        color={C.ink}
                        value={swissQR({
                          iban: soc?.iban || SOC.iban,
                          nom: titulaire.nom,
                          adr: titulaire.adr,
                          npa: titulaire.npa,
                          amount: montantQR,
                          clientNom: client?.nom,
                          clientAdr: client?.adr,
                          clientNpa: client?.npa,
                          refType: dref.type,
                          ref: dref.ref,
                          msg:
                            (isFac ? tr('Facture') : tr('Acompte offre n°')) +
                            ' ' +
                            (doc.num || ''),
                        })}
                      />
                    </div>
                    <div style={{ fontSize: 7 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: 6,
                          marginBottom: 1,
                        }}
                      >
                        {tr('Compte / payable à')}
                      </div>
                      <div style={{ fontSize: 7, lineHeight: 1.3, marginBottom: 5 }}>
                        {soc?.iban || SOC.iban}
                        <br />
                        {titulaire.nom}
                        <br />
                        {titulaire.adr}
                        <br />
                        {titulaire.npa}
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: 6,
                          marginBottom: 1,
                        }}
                      >
                        {tr('Référence')}
                      </div>
                      <div style={{ fontSize: 7, marginBottom: 5 }}>{dref.aff}</div>
                      <div
                        style={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: 6,
                          marginBottom: 1,
                        }}
                      >
                        {tr('Payable par')}
                      </div>
                      <div style={{ fontSize: 7, lineHeight: 1.3, marginBottom: 5 }}>
                        {client?.nom}
                        <br />
                        {client?.adr}
                        <br />
                        {client?.npa}
                      </div>
                      <div style={{ display: 'flex', gap: 18, marginTop: 7 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 6.5 }}>{tr('Monnaie')}</div>
                          <div style={{ fontSize: 8.5 }}>{DEVISE}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 6.5 }}>{tr('Montant')}</div>
                          <div style={{ fontSize: 8.5, fontWeight: 700 }}>{fmt(montantQR)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScaleFit>
          )}
          {ligneLegale(soc) && (
            <div
              className="doc-mentions-legales"
              style={{
                marginTop: 10,
                paddingTop: 8,
                borderTop: '1px solid ' + C.border,
                textAlign: 'center',
                fontSize: 6.5,
                color: swiss ? C.grey : C.muted,
                lineHeight: 1.6,
              }}
            >
              {ligneLegale(soc)}
            </div>
          )}
          {/* Le message libre de la pièce, tout en bas, sous les coordonnées
              de l'entreprise : une précision, un rappel, un mot au client. */}
          {String(doc.piedMessage || '').trim() && (
            <div
              className="doc-message-pied"
              style={{
                marginTop: 8,
                textAlign: 'center',
                fontSize: 8,
                color: C.ink,
                lineHeight: 1.5,
                whiteSpace: 'pre-line',
                overflowWrap: 'anywhere',
                // La couleur et le gras choisis sous le message.
                ...styleDuMessage(doc.stylesMessages, 'bas'),
              }}
            >
              {String(doc.piedMessage).trim()}
            </div>
          )}
          {annexe && !statique && (
            // Les photos jointes à l'offre, en annexe comme dans le PDF que le
            // client reçoit ; à l'impression, elles partent sur une page à part.
            <div
              className="doc-annexe"
              style={{
                breakBefore: 'page',
                pageBreakBefore: 'always',
                marginTop: 18,
                paddingTop: 10,
                borderTop: '1px dashed ' + C.border,
              }}
            >
              {annexe}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
